import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';
import isNil from 'lodash/isNil';
import * as Yup from 'yup';
import { DATE_FORMAT } from './system';



const FIELD_NAMES_MAP = {
  RELEASED_AT: 'released at',
  RELEASED_TO: 'released to',
  RELEASED_FROM: 'released from',
};

// Test names
const TEST_NAMES_MAP = {
  DATE_AFTER: 'test_date_after',
  DATE_BEFORE: 'test_date_before',
  DAY_IN_THE_PAST: 'test_day_in_the_past',
  DATE_AVAILABLE: 'test_date_available',
};

/**
 * Generates an error message for "same or before" date validation
 * @param {string} dateFrom - Name of the dateFrom date field
 * @param {string} dateTo - Name of the dateTo date field
 * @returns {string} Formatted error message
 */
const shouldBeSameOrBeforeErrorMessage = (dateFrom, dateTo) => {
  return `${capitalize(dateFrom)} date must be same or before than ${dateTo}`;
};

/**
 * Generates an error message for "same or after" date validation
 * @param {string} dateTo - Name of the dateTo date field
 * @param {string} dateFrom - Name of the dateFrom date field
 * @returns {string} Formatted error message
 */
const shouldBeSameOrAfterErrorMessage = (dateTo, dateFrom) => {
  return `${capitalize(dateTo)} date must be same or after than ${dateFrom}`;
};

/**
 * Generates an error message for required date field
 * @param {string} fieldName - Name of the date field
 * @returns {string} Formatted error message
 */
const requiredErrorMessage = (fieldName) => `Required to specify ${fieldName} date`;

/**
 * Generates an error message for "after yesterday" validation
 * @param {string} fieldName - Name of the date field
 * @returns {string} Formatted error message
 */
const shouldBeSameOrAfterYesterdayErrorMessage = (fieldName) => {
  return `${capitalize(fieldName)} date must be same or after than yesterday`;
};

/**
 * Predefined error messages for common date validation scenarios
 * @typedef {Object} ErrorMessages
 * @property {Object} releasedAt - Error messages for 'released at' field
 * @property {string} releasedAt.before - Error when released at is after released to
 * @property {string} releasedAt.afterYesterday - Error when released at is before yesterday
 * @property {string} releasedAt.required - Error when released at is not specified
 * @property {Object} releasedTo - Error messages for 'released to' field
 * @property {string} releasedTo.after - Error when released to is before released at
 * @property {string} releasedTo.afterYesterday - Error when released to is before yesterday
 * @property {string} releasedTo.required - Error when released to is not specified
 * @property {Object} releasedFrom - Error messages for 'released from' field
 * @property {string} releasedFrom.before - Error when released from is after released to
 * @property {string} releasedFrom.required - Error when released from is not specified
 */
export const errorMessages = {
  releasedAt: {
    before: shouldBeSameOrBeforeErrorMessage(FIELD_NAMES_MAP.RELEASED_AT, FIELD_NAMES_MAP.RELEASED_TO),
    afterYesterday: shouldBeSameOrAfterYesterdayErrorMessage(FIELD_NAMES_MAP.RELEASED_AT),
    required: requiredErrorMessage(FIELD_NAMES_MAP.RELEASED_AT),
  },
  releasedTo: {
    after: shouldBeSameOrAfterErrorMessage(FIELD_NAMES_MAP.RELEASED_TO, FIELD_NAMES_MAP.RELEASED_AT),
    afterYesterday: shouldBeSameOrAfterYesterdayErrorMessage(FIELD_NAMES_MAP.RELEASED_TO),
    required: requiredErrorMessage(FIELD_NAMES_MAP.RELEASED_TO),
  },
  releasedFrom: {
    before: shouldBeSameOrBeforeErrorMessage(FIELD_NAMES_MAP.RELEASED_FROM, FIELD_NAMES_MAP.RELEASED_TO),
    required: requiredErrorMessage(FIELD_NAMES_MAP.RELEASED_FROM),
  },
};

/**
 * Decorator function for adding custom validation tests to Yup schema
 * Skips validation if the value matches the default value
 * 
 * @param {string} testName - Unique name identifier for the test
 * @param {string} errorMessage - Error message to display when validation fails
 * @param {Function} testFunction - Validation function that returns true if valid
 * @returns {Object} Yup test schema
 * @this {Object} Yup schema context
 */

function decorate (testName, errorMessage, testFunction) {
  const defaultValue = this.getDefault();

  return this.test(testName, errorMessage, (value) => {
    if (dayjs(defaultValue).isSame(value)) {
      return true;
    }

    return testFunction(value);
  });
}

/**
 * Helper function to check if all date values in an array are empty
 * 
 * @param {Array<string|Date|null>} dates - Array of dates to check
 * @returns {boolean} True if all dates in the array are empty, false otherwise
 */

const isSomeDateEmpty = (dates) => dates.some((date) => isNil(date) || isEmpty(dayjs(date)));

/**
 * Yup validation test for checking if a date is the same or after another date
 * Returns true if either date is empty or if the value is the same or after the comparison date
 * 
 * @param {string|Date} date - Date to compare against
 * @param {string} errorMessage - Error message to display when validation fails
 * @returns {Object} Yup test schema
 * @this {Object} Yup schema context
 * 
 * @example
 * Yup.date().after(values?.released_at, errorMessages.releasedTo.after)
 */

function testDayIsSameOrAfter (date, errorMessage) {
  return decorate.call(this, TEST_NAMES_MAP.DATE_AFTER, errorMessage, (value) => {
    if (isSomeDateEmpty([ date, value ])) {
      return true;
    }

    return dayjs(value).isSameOrAfter(date);
  });
}

/**
 * Yup validation test for checking if a date is the same or before another date
 * Returns true if either date is empty or if the value is the same or before the comparison date
 * 
 * @param {string|Date} date - Date to compare against
 * @param {string} errorMessage - Error message to display when validation fails
 * @returns {Object} Yup test schema
 * @this {Object} Yup schema context
 * 
 * @example
 * Yup.date().before(values?.released_to, errorMessages.releasedAt.before)
 */

function testDayIsSameOrBefore (date, errorMessage) {
  return decorate.call(this, TEST_NAMES_MAP.DATE_BEFORE, errorMessage, (value) => {
    if (isSomeDateEmpty([ date, value ])) {
      return true;
    }

    return dayjs(value).isSameOrBefore(date);
  });
}

/**
 * Yup validation test for checking if a date is after a specific number of days before today
 * Used to prevent setting dates too far in the past
 * 
 * @param {string} errorMessage - Error message to display when validation fails
 * @param {number} daysBefore - Number of days before today to use as cutoff
 * @param {Object} options - Configuration options
 * @param {boolean} [options.allowUpdateInThePast=false] - If true, skips validation
 * @param {boolean} [options.isUtc=false] - If true, uses UTC timezone for comparison
 * @returns {Object} Yup test schema
 * @this {Object} Yup schema context
 * 
 * @example
 * Yup.date().afterDaysBefore('Date must be within 7 days', 7, { allowUpdateInThePast: false, isUtc: true })
 */

function testAfterDaysBefore (errorMessage, daysBefore, { allowUpdateInThePast = false, isUtc = false } = {}) {
  return decorate.call(this, TEST_NAMES_MAP.DAY_IN_THE_PAST, errorMessage, (value) => {
    if (allowUpdateInThePast) {
      return true;
    }

    const cutoffDate = dayjs()
      .subtract(daysBefore, 'days')
      .startOf('day')
      .utc(isUtc);

    return !dayjs(value).isBefore(cutoffDate);
  });
}

/**
 * Yup validation test for checking if a date is after or the same as yesterday
 * Convenience wrapper around testAfterDaysBefore with daysBefore=1
 * 
 * @param {string} errorMessage - Error message to display when validation fails
 * @param {Object} options - Configuration options
 * @param {boolean} [options.allowUpdateInThePast=false] - If true, skips validation
 * @returns {Object} Yup test schema
 * @this {Object} Yup schema context
 * 
 * @example
 * Yup.date().afterYesterday(errorMessages.releasedAt.afterYesterday, { allowUpdateInThePast: false })
 */

function testAfterOrSameThanYesterday (errorMessage, { allowUpdateInThePast = false } = {}) {
  return testAfterDaysBefore.call(this, errorMessage, 1, { allowUpdateInThePast });
}

/**
 * Yup validation test for checking if a date is not in a list of unavailable dates
 * Used to prevent selecting dates that are already taken or blocked
 * 
 * @param {string[]} [dates=[]] - Array of date strings in DATE_FORMAT to check against
 * @returns {Object} Yup test schema
 * @this {Object} Yup schema context
 * 
 * @example
 * Yup.date().makeUnavailable(['2024-01-01', '2024-01-15', '2024-02-01'])
 */

function testUnavailableDates (dates = []) {
  return decorate.call(this, TEST_NAMES_MAP.DATE_AVAILABLE, 'This date is not available', (value) => {
    const date = dayjs(value);

    if (isEmpty(date)) {
      return true;
    }

    const formattedDate = date.format(DATE_FORMAT);

    return !dates.includes(formattedDate);
  });
}

// Register custom Yup methods for date validation
Yup.addMethod(Yup.date, 'before', testDayIsSameOrBefore);
Yup.addMethod(Yup.date, 'after', testDayIsSameOrAfter);
Yup.addMethod(Yup.date, 'afterYesterday', testAfterOrSameThanYesterday);
Yup.addMethod(Yup.date, 'afterDaysBefore', testAfterDaysBefore);
Yup.addMethod(Yup.date, 'makeUnavailable', testUnavailableDates);
